<!-- ============================================================
     FEE MANAGEMENT — Structures · Collection · Records · Concessions
     ============================================================ -->
<div class="fee-page">

  <!-- ── Toast ── -->
  @if (saveSuccess()) {
    <div class="fee-toast alert alert-success d-flex align-items-center gap-2 shadow">
      <i class="bi bi-check-circle-fill fs-5"></i><strong>{{ saveSuccess() }}</strong>
    </div>
  }

  <!-- ── Delete Modal ── -->
  @if (showDeleteModal()) {
    <div class="fee-overlay" (click)="showDeleteModal.set(false)">
      <div class="fee-modal fee-modal-sm" (click)="$event.stopPropagation()">
        <div class="fmh bg-danger"><div class="fmh-icon"><i class="bi bi-trash3-fill"></i></div>
          <div><h5 class="fmh-title">Delete {{ deleteEntityLabel() }}?</h5>
            <p class="fmh-sub">{{ deleteContext()?.name }}</p></div>
          <button class="fmh-close" (click)="showDeleteModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="fmb p-4 text-center">
          <p class="text-muted small mb-0">This action cannot be undone.</p>
        </div>
        <div class="fmf"><button class="btn btn-outline-secondary px-4" (click)="showDeleteModal.set(false)">Cancel</button>
          <button class="btn btn-danger px-4" (click)="executeDelete()"><i class="bi bi-trash3 me-2"></i>Delete</button></div>
      </div>
    </div>
  }

  <!-- ── Receipt Modal ── -->
  @if (showReceiptModal() && viewingReceipt()) {
    @let r = viewingReceipt()!;
    <div class="fee-overlay" (click)="showReceiptModal.set(false)">
      <div class="receipt-modal" (click)="$event.stopPropagation()">
        <div class="rm-header">
          <div class="rm-school-info">
            <div class="rm-logo"><i class="bi bi-mortarboard-fill"></i></div>
            <div><div class="rm-school">{{user.institute.inst_name}}</div>
              <div class="rm-school-sub">Fee Payment Receipt · AY {{ r.academic_year }}</div></div>
          </div>
          <div class="rm-receipt-no">
            <div class="small text-muted">Receipt No.</div>
            <div class="fw-bold font-monospace">{{ r.receipt_no }}</div>
            <span class="rm-status-badge {{ getStatusClass(r.status) }}">
              <i class="bi {{ getStatusIcon(r.status) }} me-1"></i>{{ r.status }}
            </span>
          </div>
        </div>
        <div class="rm-body" style="height: calc(100vh - 216px); overflow: auto;">
          <div class="rm-student-row">
            <div class="rm-avatar">{{ r.student_name.charAt(0) }}</div>
            <div>
              <div class="fw-semibold">{{ r.student_name }}</div>
              <div class="small text-muted">{{ r.class_name }} · Roll: {{ r.roll_no }} · ID: {{ r.student_id }}</div>
            </div>
            <div class="ms-auto text-end">
              <!-- <div class="small text-muted">Payment Date</div> -->
              <div>Date: <span class="fw-semibold">{{ formatDate(r.payment_date) }}</span></div>
              <!-- <div class="small text-muted mt-1">Due: <span class="fw-semibold">{{ formatDate(r.due_date) }}</span></div> -->
              @if (r.period_label) {
                <div class="small text-muted">Period: <span class="fw-semibold">{{ getItShort(r.period_label) }}</span></div>
              }
              <!-- <div class="small text-muted mt-1">Mode: <span class="fw-semibold">{{ r.payment_mode }}</span></div> -->
              @if (r.status !== 'Refunded') {
                <div class="small mt-1">
                  @if (r.payment_verified) {
                    <span class="verify-chip received"><i class="bi bi-shield-check me-1"></i>{{ r.payment_mode }} Payment Received</span>
                  } @else {
                    <span class="verify-chip awaiting"><i class="bi bi-hourglass-split me-1"></i>Awaiting Confirmation</span>
                  }
                </div>
              }
            </div>
          </div>
          @if (!r.payment_verified) {
            <div class="verify-banner">
              <i class="bi bi-exclamation-circle me-2"></i>
              This {{ r.payment_mode }} payment has not been confirmed. Heads stay unlocked until it is marked received.
              @if (r.total_paid > 0) {
                <button type="button" class="btn btn-sm fee-btn-primary ms-auto" (click)="verifyPayment(r.id)">
                  <i class="bi bi-check2-circle me-1"></i>Mark received
                </button>
              }
            </div>
          }
          <!-- Fee Details Table -->
          <table class="rm-table">
            <thead><tr><th>#</th><th>Fee Head</th><th>Period</th><th class="text-end">Due</th><th class="text-end">Concession</th><th class="text-end">Paid</th><th class="text-end">Balance</th></tr></thead>
            <tbody>
              @for (item of r.items; track $index; let i = $index) {
                <tr>
                  <td class="text-muted small">{{ i+1 }}</td>
                  <td class="fw-semibold small">{{ item.fee_head_name }}</td>
                  <td class="small text-secondary">{{ item.period_label || r.period_label || '—' }}</td>
                  <td class="text-end small">{{ formatCurrency(item.due_amount) }}</td>
                  <td class="text-end small text-success">{{ item.concession > 0 ? '– ' + formatCurrency(item.concession) : '—' }}</td>
                  <td class="text-end small fw-semibold">{{ formatCurrency(item.paid_amount) }}</td>
                  <td class="text-end small" [class.text-danger]="item.balance > 0">{{ item.balance > 0 ? formatCurrency(item.balance) : '—' }}</td>
                </tr>
              }
            </tbody>
            <tfoot>
              <tr><td colspan="3" class="fw-bold">TOTAL</td>
                <td class="text-end fw-bold">{{ formatCurrency(r.total_due) }}</td>
                <td class="text-end text-success fw-bold">{{ r.total_concession > 0 ? '– ' + formatCurrency(r.total_concession) : '—' }}</td>
                <td class="text-end fw-bold text-primary">{{ formatCurrency(r.total_paid) }}</td>
                <td class="text-end fw-bold" [class.text-danger]="r.balance > 0">{{ r.balance > 0 ? formatCurrency(r.balance) : '—' }}</td>
              </tr>
              @if (r.refund_amount > 0) {
                <tr class="rm-refund-row">
                  <td colspan="3" class="fw-semibold">Refunded</td>
                  <td></td><td></td>
                  <td class="text-end fw-semibold">– {{ formatCurrency(r.refund_amount) }}</td>
                  <td></td>
                </tr>
                <tr class="rm-net-row">
                  <td colspan="3" class="fw-bold">Net paid</td>
                  <td></td><td></td>
                  <td class="text-end fw-bold text-primary">{{ formatCurrency(netPaid(r)) }}</td>
                  <td></td>
                </tr>
              }
            </tfoot>
          </table>
          @if (r.status === 'Partial' && r.balance > 0) {
            <div class="remaining-banner mt-3">
              <i class="bi bi-wallet2 me-1"></i>
              Previous payment {{ formatCurrency(r.total_paid) }} of {{ formatCurrency(r.total_due) }}.
              Remaining {{ formatCurrency(r.balance) }}.
              @if (canCollectRemaining(r)) {
                <button type="button" class="btn btn-sm fee-btn-primary ms-2" (click)="collectRemainingFrom(r)">Collect remaining</button>
              }
            </div>
          }
          @if (r.late_fee > 0) {
            <div class="late-note"><i class="bi bi-exclamation-triangle-fill me-1"></i>
              Late fee {{ formatCurrency(r.late_fee) }} for {{ r.days_overdue }} day{{ r.days_overdue === 1 ? '' : 's' }} after due date.
            </div>
          }
          @if (r.refund_amount > 0) {
            <div class="refund-note"><i class="bi bi-arrow-counterclockwise me-1"></i>
              Refunded {{ formatCurrency(r.refund_amount) }}
              @if (r.refund_date) { on {{ formatDate(r.refund_date) }} }
              @if (r.refund_mode) { via {{ r.refund_mode }} }
              @if (r.refund_reason) { — {{ r.refund_reason }} }
            </div>
          }
          @if (r.remarks) { <div class="rm-remarks"><i class="bi bi-chat-text me-1"></i>{{ r.remarks }}</div> }
        </div>
        <div class="rm-footer">
          <div class="rm-sig">Authorised Signatory</div>
          <div class="d-flex gap-2 flex-wrap">
            @if (canCollectRemaining(r)) {
              <button class="btn btn-outline-warning btn-sm" (click)="collectRemainingFrom(r)"><i class="bi bi-cash-stack me-1"></i>Collect remaining</button>
            }
            @if (canEditCollection(r)) {
              <button class="btn btn-outline-primary btn-sm" (click)="editCollection(r)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
            }
            @if (canRefund(r)) {
              <button class="btn btn-outline-danger btn-sm" (click)="openRefund(r)"><i class="bi bi-arrow-counterclockwise me-1"></i>Refund</button>
            }
            <button class="btn btn-outline-danger btn-sm" (click)="deleteCollection(r)"><i class="bi bi-trash3-fill me-1"></i>Delete</button>
            <button class="btn btn-outline-secondary btn-sm" (click)="showReceiptModal.set(false)">Close</button>
            <button class="btn fee-btn-primary btn-sm" (click)="printReceipt()"><i class="bi bi-printer-fill me-1"></i>Print</button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ── Refund Modal ── -->
  @if (showRefundModal()) {
    <div class="fee-overlay refund-overlay" (click)="showRefundModal.set(false)">
      <div class="fee-modal" (click)="$event.stopPropagation()">
        <div class="fmh bg-refund">
          <div class="fmh-icon"><i class="bi bi-arrow-counterclockwise"></i></div>
          <div>
            <h5 class="fmh-title">Refund Fee</h5>
            <p class="fmh-sub">Return collected fee against this receipt</p>
          </div>
          <button class="fmh-close" (click)="showRefundModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="fmb p-4">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label fw-semibold">Refund Amount <span class="req">*</span></label>
              <input type="number" class="form-control" [(ngModel)]="refundForm.amount" min="1"
                [class.is-invalid]="refundErrors['amount']">
              @if (refundErrors['amount']) { <div class="invalid-feedback">{{ refundErrors['amount'] }}</div> }
            </div>
            <div class="col-md-6">
              <label class="form-label fw-semibold">Refund Date <span class="req">*</span></label>
              <input type="date" class="form-control" [(ngModel)]="refundForm.date"
                [class.is-invalid]="refundErrors['date']">
              @if (refundErrors['date']) { <div class="invalid-feedback">{{ refundErrors['date'] }}</div> }
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold">Refund Mode</label>
              <select class="form-select" [(ngModel)]="refundForm.mode">
                @for (mode of paymentModes; track mode) { <option [value]="mode">{{ mode }}</option> }
              </select>
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold">Reason <span class="req">*</span></label>
              <textarea class="form-control" rows="2" maxlength="200" [(ngModel)]="refundForm.reason"
                placeholder="e.g. Student withdrawn, duplicate payment"
                [class.is-invalid]="refundErrors['reason']"></textarea>
              @if (refundErrors['reason']) { <div class="invalid-feedback">{{ refundErrors['reason'] }}</div> }
            </div>
          </div>
        </div>
        <div class="fmf">
          <button class="btn btn-outline-secondary px-4" (click)="showRefundModal.set(false)">Cancel</button>
          <button class="btn btn-danger px-4" (click)="submitRefund()"><i class="bi bi-arrow-counterclockwise me-2"></i>Confirm Refund</button>
        </div>
      </div>
    </div>
  }

  <!-- ===================== PAGE HEADER ===================== -->
  <div class="fee-ph mb-4">
    <div>
      <h1 class="fee-title"><i class="bi bi-cash-stack me-2 text-primary" style="font-size:1.1rem;"></i>Fee Management</h1>
      <p class="fee-subtitle">Fee Periodic · Fee structures · Collection · Records · Ledger · Concessions</p>
    </div>
    @if (activeTab() === 'periodic' && periodFormMode === 'list') {
      <button class="btn fee-btn-primary btn-sm" (click)="openNewPeriodic()">
        <i class="bi bi-plus-circle-fill me-1"></i>Add Fee Period
      </button>
    }
    @if (activeTab() === 'structures' && structureFormMode === 'list') {
      <button class="btn fee-btn-primary btn-sm" (click)="openNewStructure()">
        <i class="bi bi-plus-circle-fill me-1"></i>Add Fee Structure
      </button>
    }
    @if (activeTab() === 'concessions' && concessionFormMode === 'list') {
      <button class="btn fee-btn-primary btn-sm" (click)="openNewConcession()">
        <i class="bi bi-plus-circle-fill me-1"></i>Add Concession
      </button>
    }
  </div>

  <!-- ===================== TABS ===================== -->
  <div class="fee-tabs mb-4">
    @for (tab of [{k:'periodic',l:'Fee Periodic',ic:'bi-calendar2-range'},{k:'structures',l:'Fee Structures',ic:'bi-receipt-cutoff'},{k:'collect',l:'Collect Fee',ic:'bi-cash-coin'},{k:'records',l:'Records',ic:'bi-table'},{k:'ledger',l:'Fee Ledger',ic:'bi-journal-text'},{k:'concessions',l:'Concessions',ic:'bi-percent'}]; track tab.k) {
      <button class="ftab" [class.active]="activeTab() === tab.k" (click)="activeTab.set($any(tab.k)); structureFormMode='list'; concessionFormMode='list'; periodFormMode='list'">
        <i class="bi {{ tab.ic }} me-1"></i>{{ tab.l }}
      </button>
    }
  </div>

  <!-- ===================================================
       TAB 1: FEE STRUCTURES
       =================================================== -->
  @if (activeTab() === 'structures') {

    <!-- LIST VIEW -->
    @if (structureFormMode === 'list') {
      @if (feeStructures().length === 0) {
        <div class="empty-state"><i class="bi bi-receipt-cutoff"></i><p>No fee structures created</p>
          <button class="btn fee-btn-primary btn-sm" (click)="openNewStructure()"><i class="bi bi-plus-circle me-1"></i>Create First Structure</button></div>
      } @else {
        <div class="fs-grid">
          @for (s of feeStructures(); track s.id) {
            <div class="fs-card">
              <div class="fsc-header">
                <div>
                  <h6 class="fsc-class">{{ dataService.getClassName(s.class_name) }} Class</h6>
                  @if (s.program_name) { <div class="fsc-program">{{ s.program_name }}</div> }
                  <span class="fsc-year">AY {{ s.academic_year }}</span>
                </div>
                <div class="d-flex flex-column align-items-end gap-1">
                  <span class="fsc-total">{{ formatCurrency(s.total_amount) }}</span>
                  <span class="fsc-status" [class.status-active]="s.is_active" [class.status-inactive]="!s.is_active">{{ s.is_active ? 'Active' : 'Inactive' }}</span>
                </div>
              </div>
              <div class="fsc-items">
                @for (item of s.items; track item.fee_head_code) {
                  <div class="fsi-row">
                    <span class="fsi-name">{{ item.fee_head_name }}</span>
                    <span class="fsi-freq">{{ item.frequency }}</span>
                    <span class="fsi-amount">{{ formatCurrency(item.amount) }}</span>
                    @if (!item.is_mandatory) { <span class="fsi-optional">optional</span> }
                    @else { <span class="fsi-mandatory">required</span> }
                  </div>
                }
              </div>
              <div class="fsc-footer">
                <button class="fcf-btn edit"   (click)="editStructure(s)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
                <button class="fcf-btn delete" (click)="deleteStructure(s.id, s.class_name + ' – ' + s.academic_year)"><i class="bi bi-trash3-fill"></i></button>
              </div>
            </div>
          }
        </div>
      }
    }

    <!-- FORM VIEW -->
    @if (structureFormMode === 'form') {
      <div class="fee-card">
        <div class="fc-header">
          <button class="btn btn-outline-secondary btn-sm me-3" (click)="structureFormMode='list'"><i class="bi bi-arrow-left"></i></button>
          <i class="bi bi-receipt-cutoff me-2"></i>
          {{ editingStructureId !== null ? 'Edit Fee Structure' : 'New Fee Structure' }}
        </div>
        <div class="fc-body">
          <div class="row g-3 mb-4">
            <div class="col-md-4">
              <label class="form-label fw-semibold">Class <span class="req">*</span></label>
              <!-- <select class="form-select" [(ngModel)]="structureForm.class_name" (change)="onStructureClassChange()" [class.is-invalid]="structureErrors['class_name']"> -->
              <select class="form-select" [(ngModel)]="structureForm.class_name">
                <option value="">-- Select Class --</option>
                @for (c of classOptions; track c) { <option [value]="c.code">{{ c.name }}</option> }
              </select>
              @if (structureErrors['class_name']) { <div class="invalid-feedback">{{ structureErrors['class_name'] }}</div> }
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold">Program Name / Title <span class="req">*</span></label>
              <input type="text" class="form-control" [(ngModel)]="structureForm.program_name">
              <!-- <select class="form-select" [(ngModel)]="structureForm.program_name"
                [disabled]="!structureForm.class_name"
                [class.is-invalid]="structureErrors['program_name']">
                <option value="">{{ structureForm.class_name ? '-- Select Program --' : 'Select a class first' }}</option>
                @for (p of programsForClass(structureForm.class_name); track p) { <option [value]="p">{{ p }}</option> }
              </select> -->
              @if (structureErrors['program_name']) { <div class="invalid-feedback">{{ structureErrors['program_name'] }}</div> }
            </div>
            <!-- <div class="col-md-2">
              <label class="form-label fw-semibold">Academic Year <span class="req">*</span></label>
              <select class="form-select" [(ngModel)]="structureForm.academic_year">
                @for (y of academicYears; track y) { <option [value]="y.code">{{ y.name }}</option> }
              </select>
            </div> -->
            <div class="col-md-2">
              <label class="form-label fw-semibold">Status</label>
              <select class="form-select" [(ngModel)]="structureForm.is_active">
                <option [value]="true">Active</option>
                <option [value]="false">Inactive</option>
              </select>
            </div>
          </div>

          <!-- Fee Items -->
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="sub-label mb-0">Fee Components</h6>
            <button type="button" class="btn btn-add-item" (click)="addStructureItem()"><i class="bi bi-plus-circle me-1"></i>Add Fee Head</button>
          </div>
          @if (structureErrors['items']) { <div class="alert alert-danger py-2 small">{{ structureErrors['items'] }}</div> }

          <div class="table-responsive">
            <table class="fee-item-table">
              <thead><tr><th>#</th><th>Fee Head</th><th>Amount (₹)</th><th>Frequency</th><th>Mandatory</th><th></th></tr></thead>
              <tbody>
                @for (item of structureForm.items; track item; let i = $index) {
                  <tr>
                    <td class="text-muted small">{{ i+1 }}</td>
                    <td>
                      <select class="form-select form-select-sm" [(ngModel)]="item.fee_head_code" (change)="onHeadSelect(item)" [name]="'head'+i">
                        <option [value]="0">-- Select --</option>
                        @for (h of feeHeads().filter(h => h.is_active); track h.id) {
                          <option [value]="h.head_code">{{ h.head_name }}</option>
                        }
                      </select>
                    </td>
                    <td>
                      <input type="number" class="form-control form-control-sm" [(ngModel)]="item.amount" min="0" placeholder="0" [name]="'amt'+i">
                    </td>
                    <td>
                      <select class="form-select form-select-sm" [(ngModel)]="item.frequency" [name]="'freq'+i">
                        @for (f of frequencies; track f) { <option [value]="f">{{ f }}</option> }
                      </select>
                    </td>
                    <td class="text-center">
                      <div class="form-check form-switch mb-0 d-flex justify-content-center">
                        <input class="form-check-input" type="checkbox" [(ngModel)]="item.is_mandatory" [name]="'mand'+i">
                      </div>
                    </td>
                    <td>
                      <button type="button" class="btn-remove-row" (click)="removeStructureItem(i)"><i class="bi bi-x-lg"></i></button>
                    </td>
                  </tr>
                }
                @if (structureForm.items.length > 0) {
                  <tr class="total-row">
                    <td colspan="2" class="fw-bold">Total</td>
                    <td class="fw-bold text-primary">{{ formatCurrency(structureFormTotal()) }}</td>
                    <td colspan="3"></td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="d-flex justify-content-end gap-3 mt-4">
            <button type="button" class="btn btn-outline-secondary px-5" (click)="structureFormMode='list'">Cancel</button>
            <button type="button" class="btn fee-btn-primary px-5" (click)="saveStructure()">
              <i class="bi bi-check-circle-fill me-2"></i>{{ editingStructureId !== null ? 'Update' : 'Save' }} Structure
            </button>
          </div>
        </div>
      </div>
    }
  }

  <!-- ===================================================
       TAB: FEE PERIODIC
       =================================================== -->
  @if (activeTab() === 'periodic') {

    @if (periodFormMode === 'list') {
      @if (feePeriodics().length === 0) {
        <div class="empty-state">
          <i class="bi bi-calendar2-range"></i><p>No fee periods defined</p>
          <button class="btn fee-btn-primary btn-sm" (click)="openNewPeriodic()">
            <i class="bi bi-plus-circle me-1"></i>Create First Fee Period
          </button>
        </div>
      } @else {
        <div class="period-grid">
          @for (p of feePeriodics(); track p.id) {
            <div class="period-card">
              <div class="pc-header">
                <div>
                  <h6 class="pc-session">Session {{ p.session }}</h6>
                  <span class="pc-kind">{{ p.period }}</span>
                </div>
                <div class="text-end">
                  <div class="pc-count">{{ p.fee_plan.length }} installment{{ p.fee_plan.length === 1 ? '' : 's' }}</div>
                  <div class="small text-muted">Grace {{ p.grace_days }} day{{ p.grace_days === 1 ? '' : 's' }}</div>
                </div>
              </div>
              <div class="pc-meta">
                <span><i class="bi bi-calendar-event me-1"></i>{{ formatDate(p.start_date) }}</span>
                <span class="text-muted">→</span>
                <span><i class="bi bi-calendar-check me-1"></i>{{ formatDate(p.end_date) }}</span>
              </div>
              <div class="pc-plan">
                @for (inst of p.fee_plan.slice(0, 6); track inst.seq) {
                  <div class="pci-row">
                    <span class="pci-label">{{ inst.label }}</span>
                    <span class="pci-last">Due by {{ formatDate(inst.last_date) }}</span>
                  </div>
                }
                @if (p.fee_plan.length > 6) {
                  <div class="pci-more">+{{ p.fee_plan.length - 6 }} more</div>
                }
              </div>
              <div class="pc-late">Late fee {{ formatCurrency(p.late_fee_per_day) }}/day after last date</div>
              <div class="fsc-footer">
                <button class="fcf-btn edit" (click)="editPeriodic(p)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
                <button class="fcf-btn delete" (click)="deletePeriodic(p.id, p.session + ' · ' + p.period)"><i class="bi bi-trash3-fill"></i></button>
              </div>
            </div>
          }
        </div>
      }
    }

    @if (periodFormMode === 'form') {
      <div class="fee-card">
        <div class="fc-header">
          <button class="btn btn-outline-secondary btn-sm me-3" (click)="periodFormMode='list'"><i class="bi bi-arrow-left"></i></button>
          <i class="bi bi-calendar2-range me-2"></i>
          {{ editingPeriodId !== null ? 'Edit Fee Period' : 'New Fee Period' }}
        </div>
        <div class="fc-body">
          <div class="row g-3 mb-4">
            <!-- <div class="col-md-3">
              <label class="form-label fw-semibold">Session <span class="req">*</span></label>
              <select class="form-select" [(ngModel)]="periodForm.session" [class.is-invalid]="periodErrors['session']">
                <option value="">-- Select Session --</option>
                @for (y of academicYears; track y) { <option [value]="y.name">{{ y.name }}</option> }
              </select>
              @if (periodErrors['session']) { <div class="invalid-feedback">{{ periodErrors['session'] }}</div> }
            </div> -->
            <div class="col-md-3">
              <label class="form-label fw-semibold">Start Date <span class="req">*</span></label>
              <input type="date" class="form-control" [(ngModel)]="periodForm.start_date"
                (change)="refreshFeePlan()" [class.is-invalid]="periodErrors['start_date']">
              @if (periodErrors['start_date']) { <div class="invalid-feedback">{{ periodErrors['start_date'] }}</div> }
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">End Date <span class="req">*</span></label>
              <input type="date" class="form-control" [(ngModel)]="periodForm.end_date"
                (change)="refreshFeePlan()" [class.is-invalid]="periodErrors['end_date']">
              @if (periodErrors['end_date']) { <div class="invalid-feedback">{{ periodErrors['end_date'] }}</div> }
            </div>
            <div class="col-md-2">
              <label class="form-label fw-semibold">Grace Days <span class="req">*</span></label>
              <input type="number" class="form-control" [(ngModel)]="periodForm.grace_days"
                min="1" max="20" (change)="refreshFeePlan()" [class.is-invalid]="periodErrors['grace_days']">
              @if (periodErrors['grace_days']) { 
                <div class="invalid-feedback">{{ periodErrors['grace_days'] }}</div> 
              }
              <!-- @else { <div class="form-text">Number from 1-20. Last date = start date + grace days.</div> } -->
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Late Fee / Day (₹)</label>
              <input type="number" class="form-control" [(ngModel)]="periodForm.late_fee_per_day"
                min="0" max="500" [class.is-invalid]="periodErrors['late_fee_per_day']"
                style="width: 100px;">
              @if (periodErrors['late_fee_per_day']) { 
                <div class="invalid-feedback">{{ periodErrors['late_fee_per_day'] }}</div> 
              }
              <!-- @else { <div class="form-text">Charged after due date, capped at 30 days.</div> } -->
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold">Period <span class="req">*</span></label>
              <div class="payment-mode-group">
                @for (kind of periodKinds; track kind) {
                  <label class="pm-option" [class.selected]="periodForm.period === kind">
                    <input type="radio" name="feePeriod" [value]="kind" [(ngModel)]="periodForm.period"
                      (change)="refreshFeePlan()" class="d-none">
                    <i class="bi bi-calendar3 d-block"></i>
                    <span>{{ kind }}</span>
                  </label>
                }
              </div>
              @if (periodErrors['period']) { <div class="text-danger small mt-1">{{ periodErrors['period'] }}</div> }
            </div>
          </div>

          <div class="d-flex align-items-center justify-content-between mb-2">
            <h6 class="sub-label mb-0">Fee Plan (JSONB)</h6>
            <span class="small text-muted">{{ periodForm.fee_plan.length }} installment{{ periodForm.fee_plan.length === 1 ? '' : 's' }} generated</span>
          </div>
          @if (periodErrors['fee_plan']) { <div class="alert alert-danger py-2 small">{{ periodErrors['fee_plan'] }}</div> }

          @if (periodForm.fee_plan.length > 0) {
            <div class="table-responsive mb-3">
              <table class="fee-item-table">
                <thead><tr><th>#</th><th>Installment</th><th>Months</th><th>Start Date</th><th>Last Date</th></tr></thead>
                <tbody>
                  @for (inst of periodForm.fee_plan; track inst.seq) {
                    <tr>
                      <td class="text-muted small">{{ inst.seq }}</td>
                      <td class="fw-semibold small">{{ inst.label }}</td>
                      <td class="small text-secondary">{{ inst.months.join(', ') }}</td>
                      <td class="small">{{ formatDate(inst.start_date) }}</td>
                      <td class="small fw-semibold">{{ formatDate(inst.last_date) }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <!-- <details class="jsonb-preview mb-3">
              <summary>View fee_plan JSONB</summary>
              <pre>{{ feePlanJson() }}</pre>
            </details> -->
          } @else {
            <div class="alert alert-warning py-2 small">Select start and end dates to generate the fee plan.</div>
          }

          <div class="d-flex justify-content-end gap-3 mt-4">
            <button type="button" class="btn btn-outline-secondary px-5" (click)="periodFormMode='list'">Cancel</button>
            <button type="button" class="btn fee-btn-primary px-5" (click)="savePeriodic()">
              <i class="bi bi-check-circle-fill me-2"></i>{{ editingPeriodId !== null ? 'Update' : 'Save' }} Period
            </button>
          </div>
        </div>
      </div>
    }
  }

  <!-- ===================================================
       TAB 2: COLLECT FEE
       =================================================== -->
  @if (activeTab() === 'collect') {
    <div class="row g-4">

      <!-- Left: Collection Form -->
      <div class="col-xl-7">
        <div class="fee-card">
          <div class="fc-header d-flex align-items-center justify-content-between py-2">
            <span><i class="bi bi-cash-coin me-2"></i>{{ editingReceiptId !== null ? 'Edit Fee Collection' : 'Fee Collection Form' }}</span>
            {{collectForm.academic_year}}
            <!-- <select class="form-select py-0 my-0" [(ngModel)]="collectForm.academic_year" (change)="loadFeeItems()"
              style="width: 150px;">
              @for (y of academicYears; track y) { 
                <option [value]="y.code" [selected]="y.code==recordYear()">{{ y.name }}</option> 
              }
            </select> -->
          </div>
          <div class="fc-body">
            @if (editingReceiptId !== null) {
              <div class="alert alert-info py-2 small d-flex align-items-center gap-2">
                <i class="bi bi-pencil-fill"></i>
                Editing receipt <strong>{{ editingReceiptNo() }}</strong>
                <button type="button" class="btn btn-link btn-sm ms-auto py-0" (click)="resetCollect()">Cancel edit</button>
              </div>
            }

            <div class="row g-3 mb-2">
              <div class="col-md-4">
                <label class="form-label fw-semibold">Payment Date <span class="req">*</span></label>
                <input type="date" class="form-control" [(ngModel)]="collectForm.payment_date"
                  (ngModelChange)="onPaymentDateChange()"
                  [class.is-invalid]="collectSubmitted && collectErrors['payment_date']">
              </div>

              <!-- Student Lookup -->
              <div class="col-md-8 student-lookup">
                <label class="form-label fw-semibold">Roll No./Admn.No./Name <span class="req">*</span></label>
                <div class="input-group">
                  <span class="input-group-text bg-light"><i class="bi bi-person-fill text-muted"></i></span>
                  <input type="text" class="form-control" [(ngModel)]="std_kw"
                    (keyup.enter)="loadStudent()"
                    [class.is-invalid]="collectSubmitted && collectErrors['student_name']">
                  <button class="btn fee-btn-primary" (click)="loadStudent()"><i class="bi bi-search me-1"></i>Fetch</button>
                </div>
                @if (collectSubmitted && collectErrors['student_name']) {
                  <div class="text-danger small mt-1">{{ collectErrors['student_name'] }}</div>
                }
                <!-- <div class="form-text">Try: S001, S002, S003, S009, S010</div> -->
              </div>
            </div>

            <!-- Student Details -->
            <div class="student-{{studentLoaded ? 'info' : 'none'}}-strip mb-4">
              @if (studentLoaded) {
                <div class="sis-avatar">{{ collectForm.student_name.charAt(0) }}</div>
                <div>
                  <div class="sis-name">{{ collectForm.student_name }}</div>
                  <div class="sis-meta">Class: {{ dataService.getClassName(collectForm.class_name) }} · Roll: {{ collectForm.roll_no }}</div>
                </div>
                <div class="ms-auto">
                  <span class="sis-badge">{{ collectForm.academic_year }}</span>
                </div>
              } @else {
                <h1>?</h1>
                <div>
                  <div class="sis-name">No Student Selected!</div>
                  <div class="sis-meta">Please select student to get FEE payment.</div>
                </div>
              }
            </div>

            <!-- Payment Details -->
            <div class="row g-3 mb-4">
              <!--div class="col-md-6">
                <label class="form-label fw-semibold">Academic Year</label>
                <select class="form-select" [(ngModel)]="collectForm.academic_year" (change)="loadFeeItems()">
                  @for (y of academicYears; track y) { 
                    <option [value]="y.code" [selected]="y.code==recordYear()">{{ y.name }}</option> 
                  }
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Payment Date <span class="req">*</span></label>
                <input type="date" class="form-control" [(ngModel)]="collectForm.payment_date"
                  (ngModelChange)="onPaymentDateChange()"
                  [class.is-invalid]="collectSubmitted && collectErrors['payment_date']">
              </div>

              <div class="col-md-3">
                <label class="form-label fw-semibold">Payment Period</label>
                <select class="form-select" [(ngModel)]="collectForm.period_seq"
                  (change)="onCollectPeriodChange()"> 
                  <option [ngValue]="null">{{ collectInstallments().length ? '-- Select period --' : 'No fee period for this session' }}</option>
                  @for (inst of collectInstallments(); track inst.seq) {
                    <option [ngValue]="inst.seq">{{ inst.label }}</option>
                  }
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label fw-semibold">Due Date</label>
                <input type="date" class="form-control" [value]="collectForm.due_date" readonly>
              </div> -->

              <!-- Pay in advance -->
              <div class="d-flex justify-content-between">
                <div>
                  <label for="all">
                    <input type="checkbox" id="all" (change)="handleAllSelection($event)"> Select All Months
                  </label>
                </div>
                <label class="form-label fw-semibold mb-0">Select Month:</label>
              </div>
              @for (iMon of collectInstallments(); track iMon.seq) {
                <div class="col-3 mt-2">
                  <label for="{{iMon.seq}}">
                    <input type="checkbox" class="cis me-1" id="{{iMon.seq}}" value="{{iMon.seq}}"
                      (change)="handleSelection($event, iMon)">{{iMon.label.split(' ')[0]}}
                  </label>
                </div>
              }
              <!-- {{targetPeriods() | json}} -->

              <!-- <div class="col-12">
                <label class="form-label fw-semibold">Pay in advance</label>
                <div class="payment-mode-group">
                  @for (opt of advanceModes; track opt.id) {
                    <label class="pm-option" [class.selected]="collectForm.advance === opt.id">
                      [class.disabled]="(opt.id === 'next_month' && !canAdvanceMonth()) || (opt.id === 'next_quarter' && !canAdvanceQuarter())"
                      <input type="radio" name="advancePay" [value]="opt.id" [(ngModel)]="collectForm.advance"
                        (change)="onAdvanceChange()" class="d-none">
                        [disabled]="(opt.id === 'next_month' && !canAdvanceMonth()) || (opt.id === 'next_quarter' && !canAdvanceQuarter())"
                      <i class="bi d-block"
                        [class.bi-calendar-check]="opt.id === 'none'"
                        [class.bi-calendar-plus]="opt.id === 'next_month'"
                        [class.bi-calendar3]="opt.id === 'next_quarter'"></i>
                      <span>{{ opt.label }}</span>
                    </label>
                  }
                </div>
                @if (advanceSummary()) {
                  <div class="form-text mt-1">Including in advance: <strong>{{ advanceSummary() }}</strong></div>
                }
                @if (targetPeriods().length > 1) {
                  <div class="period-chip-row mt-2">
                    @for (p of targetPeriods(); track p.seq) {
                      <span class="period-chip">{{ p.label }}</span>
                    }
                  </div>
                }
              </div>-->

              <!-- Payment Mode -->
              <div class="col-12">
                <label class="form-label fw-semibold">Payment Mode</label>
                <div class="payment-mode-group">
                  @for (mode of paymentModes; track mode) {
                    <label class="pm-option" [class.selected]="collectForm.payment_mode === mode">
                      <input type="radio" name="pmode" [value]="mode" [(ngModel)]="collectForm.payment_mode"
                        (change)="onPaymentModeChange()" class="d-none">
                      <i class="bi {{ getModeIcon(mode) }} d-block"></i>
                      <span>{{ mode }}</span>
                    </label>
                  }
                </div>
              </div>
              @if (isNonCash()) {
                <div class="col-12">
                  <label class="form-label fw-semibold">Payment received?</label>
                  <div class="payment-mode-group">
                    <label class="pm-option" [class.selected]="collectForm.payment_verified"
                      (click)="collectForm.payment_verified = true">
                      <i class="bi bi-check-circle-fill d-block"></i>
                      <span>Yes, received</span>
                    </label>
                    <label class="pm-option" [class.selected]="!collectForm.payment_verified"
                      (click)="collectForm.payment_verified = false">
                      <i class="bi bi-hourglass-split d-block"></i>
                      <span>Not yet — pending</span>
                    </label>
                  </div>
                  @if (!collectForm.payment_verified) {
                    <div class="form-text text-warning-emphasis">Receipt will be saved as Pending. Fee heads stay unlocked until you confirm the payment was received.</div>
                  }
                </div>
                <!-- Cheque / DD fields -->
                @if (['Online/UPI','Cheque','DD','NEFT/RTGS'].includes(collectForm.payment_mode)) {
                  <div class="col-md-5">
                    <label class="form-label fw-semibold">Cheque / DD / Txn No.</label>
                    <input type="text" class="form-control" [(ngModel)]="collectForm.cheque_dd_no" placeholder="Reference number">
                  </div>
                  <div class="col-md-5">
                    <label class="form-label fw-semibold">Bank Name</label>
                    <input type="text" class="form-control" [(ngModel)]="collectForm.bank_name" placeholder="Bank name">
                  </div>
                }
              }
              <!-- Concession -->
              <div class="col-md-6">
                <label class="form-label fw-semibold">Apply Concession</label>
                <select class="form-select" [(ngModel)]="collectForm.concession_id" (change)="applyConcession()">
                  <option [value]="null">No Concession</option>
                  @for (c of concessions().filter(c => c.is_active); track c.id) {
                    <option [value]="c.id">{{ c.concession_name }}</option>
                  }
                </select>
              </div>
              <!-- Remarks -->
              <div class="col-md-6">
                <label class="form-label fw-semibold">Remarks</label>
                <input type="text" class="form-control" [(ngModel)]="collectForm.remarks" maxlength="200" placeholder="Optional notes">
              </div>
            </div>

            <!-- Fee Items -->
            @if (collectForm.items.length > 0 && targetPeriods().length > 0) {
              @if (previousPaymentSummary(); as prev) {
                <div class="remaining-banner mb-3">
                  <div class="d-flex align-items-start gap-2">
                    <i class="bi bi-wallet2 mt-1"></i>
                    <div class="flex-grow-1">
                      <div class="fw-semibold">Partial payment on file — collect the remaining amount</div>
                      @for (p of prev.receipts; track p.receipt_no) {
                        <div class="remain-prev mt-1">
                          <span class="font-monospace">{{ p.receipt_no }}</span>
                          · {{ formatDate(p.payment_date) }}
                          @if (p.period_label) { · {{ p.period_label }} }
                          <div class="remain-split">
                            <span>Previous payment <strong>{{ formatCurrency(p.total_paid) }}</strong>
                              of {{ formatCurrency(p.total_due) }}
                              @if (p.total_concession > 0) { (concession {{ formatCurrency(p.total_concession) }}) }</span>
                            <span class="text-danger">Remaining {{ formatCurrency(p.remaining) }}</span>
                          </div>
                        </div>
                      }
                      <div class="remain-split mt-2">
                        <span>New remaining payments <strong>{{ formatCurrency(prev.remaining) }}</strong></span>
                        @if (daysLate() > 0) {
                          <span class="text-danger">Late fee {{ formatCurrency(collectForm.late_fee) }}
                            ({{ daysLate() }} day{{ daysLate() === 1 ? '' : 's' }} after due date — edit or waive below)</span>
                        }
                      </div>
                    </div>
                    <button type="button" class="btn fee-btn-primary btn-sm flex-shrink-0" (click)="collectRemaining()">
                      <i class="bi bi-cash-stack me-1"></i>Collect remaining {{ formatCurrency(remainingCollectable()) }}
                    </button>
                  </div>
                </div>
              }
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="sub-label mb-0">Fee Items</h6>
                <div class="d-flex gap-2">
                  @if (remainingCollectable() > 0) {
                    <button type="button" class="btn btn-sm btn-outline-warning" (click)="collectRemaining()"><i class="bi bi-cash-stack me-1"></i>Remaining</button>
                  }
                  <button type="button" class="btn btn-sm btn-outline-success" (click)="payAll()"><i class="bi bi-check-all me-1"></i>Pay All</button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearAll()"><i class="bi bi-x me-1"></i>Clear</button>
                </div>
              </div>

              <div class="d-flex justify-content-between py-2">
                <span><b>Rec. No.</b>: {{setRecNo()}}</span>
                <span><b>Date</b>: {{collectForm.payment_date | date: 'dd MMM yyyy'}}</span>
              </div>
              <div class="table-responsive mb-2">
                <table class="collect-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Fee Head</th>
                      <th>Period</th>
                      <!-- <th>Frequency</th> -->
                      <th class="text-end">Due</th>
                      <!-- @if (hasPriorCredits()) {
                        <th class="text-end">Prev. Paid</th>
                        <th class="text-end">Remaining</th>
                      } -->
                      <th class="text-end" title="Concession">Conc.</th>
                      <th class="text-end" style="width:130px;">Amount to Pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of collectForm.items; track item.fee_head_code + '-' + item.period_seq; let idx = $index) {
                      <tr [class.ci-locked]="item.locked" [class.ci-remain]="item.in_partial && !item.locked">
                        <td>{{idx+1}}.</td>
                        <td>
                          <div class="fw-semibold small">{{ item.fee_head_name }}</div>
                          @if (item.locked) {
                            <div class="ci-lock-note"><i class="bi bi-lock-fill me-1"></i>{{ item.locked_reason }}</div>
                          } @else if (item.in_partial) {
                            <div class="ci-remain-note"><i class="bi bi-wallet2 me-1"></i>Balance after previous payment</div>
                          }
                        </td>
                        <td class="small text-secondary">{{ item.period_label || '—' }}<br><span class="freq-chip">{{ item.frequency }}</span></td>
                        <!-- <td><span class="freq-chip">{{ item.frequency }}</span></td> -->
                        <td class="text-end small" [class.custom-strike]="item.locked">{{ formatCurrency(item.original_due || item.due_amount) }}</td>
                        <!-- @if (hasPriorCredits()) {
                          <td class="text-end small">{{ item.previously_paid > 0 || item.previous_concession > 0 ? formatCurrency(item.previously_paid) : '—' }}</td>
                          <td class="text-end small" [class.text-danger]="item.in_partial && !item.locked">{{ item.locked || !item.in_partial ? '—' : formatCurrency(item.due_amount) }}</td>
                        } -->
                        <td class="text-end small text-success">{{ item.concession > 0 ? '– ' + formatCurrency(item.concession) : (item.previous_concession > 0 ? '– ' + formatCurrency(item.previous_concession) : '—') }}</td>
                        <td>
                          <input type="number" class="form-control form-control-sm text-end"
                            [(ngModel)]="item.paid_amount" [name]="'pay_'+item.fee_head_code+'_'+item.period_seq"
                            min="0" [max]="item.due_amount - item.concession"
                            placeholder="0" [disabled]="item.locked">
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              @if (daysLate() > 0) {
                <div class="late-banner mb-3">
                  <div class="d-flex align-items-start gap-2">
                    <i class="bi bi-exclamation-triangle-fill mt-1"></i>
                    <div class="flex-grow-1">
                      <div class="fw-semibold">Late fee applicable</div>
                      <div>Payment is {{ daysLate() }} day{{ daysLate() === 1 ? '' : 's' }} after the due date
                        ({{ formatCurrency(lateFeeRate()) }}/day{{ daysLate() > 30 ? ', capped at 30 days' : '' }}).</div>
                    </div>
                    <div class="late-fee-input">
                      <label class="form-label small mb-1">Late fee (₹)</label>
                      <input type="number" class="form-control form-control-sm text-end" min="0"
                        [(ngModel)]="collectForm.late_fee" name="lateFeeAmt">
                      <button type="button" class="btn btn-link btn-sm px-0" (click)="waiveLateFee()">Waive</button>
                    </div>
                  </div>
                </div>
              }

              <!-- Totals -->
              <div class="collect-totals">
                @if (collectTotal().previousPaid > 0) {
                  <div class="ct-row"><span>Previously paid</span><span>{{ formatCurrency(collectTotal().previousPaid) }}</span></div>
                }
                @if (collectTotal().remainingCollectable > 0) {
                  <div class="ct-row text-danger"><span>New remaining payments</span><span>{{ formatCurrency(collectTotal().remainingCollectable) }}</span></div>
                } @else {
                  <div class="ct-row"><span>Total Due</span><span>{{ formatCurrency(collectTotal().due) }}</span></div>
                }
                @if (collectTotal().remainingCollectable > 0 && collectTotal().due > collectTotal().remainingCollectable) {
                  <div class="ct-row"><span>Other unpaid heads</span><span>{{ formatCurrency(collectTotal().due - collectTotal().remainingCollectable) }}</span></div>
                }
                @if (collectTotal().concession > 0) {
                  <div class="ct-row text-success"><span>Concession</span><span>– {{ formatCurrency(collectTotal().concession) }}</span></div>
                }
                @if (collectTotal().late > 0) {
                  <div class="ct-row text-danger"><span>Late Fee</span><span>{{ formatCurrency(collectTotal().late) }}</span></div>
                }
                <div class="ct-row total"><span>Amount Paying</span><span class="text-primary">{{ formatCurrency(collectTotal().payable) }}</span></div>
                @if (collectTotal().balance > 0) {
                  <div class="ct-row text-danger"><span>Balance Due</span><span>{{ formatCurrency(collectTotal().balance) }}</span></div>
                }
              </div>
              @if (collectSubmitted && collectErrors['amount']) {
                <div class="alert alert-danger py-2 small mt-2">{{ collectErrors['amount'] }}</div>
              }
              @if (collectForm.items.length && collectForm.items.every(i => i.locked)) {
                <div class="alert alert-info py-2 small mt-2 mb-0">All fee heads for this period are already collected. Choose a later payment period.</div>
              }
            } @else if (studentLoaded) {
              <div class="alert alert-warning py-2 small">No fee structure found for {{ collectForm.class_name }} – {{ collectForm.academic_year }}</div>
            }

            <div class="d-flex justify-content-end gap-3 mt-4">
              <button type="button" class="btn btn-outline-secondary px-4" (click)="resetCollect()">Reset</button>
              <button type="button" class="btn fee-btn-primary px-5" (click)="submitCollection()"
                [disabled]="!studentLoaded || targetPeriods().length === 0 || collectForm.items.length === 0 || collectForm.items.every(i => i.locked)">
                <i class="bi me-2" [class.bi-check-circle-fill]="editingReceiptId !== null" [class.bi-receipt-cutoff]="editingReceiptId === null"></i>
                {{ editingReceiptId !== null ? 'Update Collection' : 'Collect & Generate Receipt' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Recent collections -->
      <div class="col-xl-5">
        <div class="fee-card">
          <div class="fc-header"><i class="bi bi-clock-history me-2"></i>Recent Collections</div>
          <div class="fc-body p-0">
            <ul class="recent-list">
              @for (r of receipts().slice(0, 8); track r.id) {
                <li class="rl-item">
                  <div class="rl-main" (click)="viewReceipt(r)">
                    <div class="rl-avatar">{{ r.student_name.charAt(0) }}</div>
                    <div class="rl-info">
                      <div class="rl-name">{{ r.student_name }}</div>
                      <div class="rl-meta">Class: {{ dataService.getClassName(r.class_name) }} · {{ r.receipt_no }}</div>
                    </div>
                    <div class="rl-right">
                      <div class="rl-amount">{{ formatCurrency(netPaid(r)) }}</div>
                      @if (r.refund_amount > 0) {
                        <div class="rl-refund">Refunded {{ formatCurrency(r.refund_amount) }}</div>
                      }
                      <span class="rl-status {{ getStatusClass(r.status) }}">{{ r.status }}</span>
                      @if (!r.payment_verified && r.status !== 'Refunded') {
                        <span class="verify-chip awaiting">Unverified</span>
                      }
                    </div>
                  </div>
                  <div class="rl-actions">
                    @if (canCollectRemaining(r)) {
                      <button type="button" class="ra remain" title="Collect remaining" (click)="collectRemainingFrom(r)"><i class="bi bi-cash-stack"></i></button>
                    }
                    @if (canEditCollection(r)) {
                      <button type="button" class="ra" title="Edit collection" (click)="editCollection(r)"><i class="bi bi-pencil-fill"></i></button>
                    }
                    <button type="button" class="ra danger" title="Delete collection" (click)="deleteCollection(r)"><i class="bi bi-trash3-fill"></i></button>
                  </div>
                </li>
              }
            </ul>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ===================================================
       TAB 3: FEE RECORDS
       =================================================== -->
  @if (activeTab() === 'records') {

    <!-- Filters -->
    <div class="fee-card mb-3">
      <div class="fc-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label class="form-label small fw-semibold text-muted">Search</label>
            <div class="input-group">
              <span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
              <input type="text" class="form-control border-start-0" placeholder="Name, receipt no., ID…"
                [value]="recordSearch()" (input)="recordSearch.set($any($event.target).value)">
              @if (recordSearch()) { <button class="btn btn-outline-secondary" (click)="recordSearch.set('')"><i class="bi bi-x"></i></button> }
            </div>
          </div>
          <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Class</label>
            <select class="form-select" [value]="recordClass()" (change)="recordClass.set($any($event.target).value)">
              <option value="">All Classes</option>
              @for (c of classOptions; track c) { <option [value]="c.name">{{ c.name }}</option> }
            </select></div>
          <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Status</label>
            <select class="form-select" [value]="recordStatus()" (change)="recordStatus.set($any($event.target).value)">
              <option value="">All</option>
              @for (s of ['Paid','Partial','Pending','Overdue','Cancelled','Refunded']; track s) { <option [value]="s">{{ s }}</option> }
            </select></div>
          <!-- <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Academic Year</label>
            <select class="form-select" [value]="recordYear()" (change)="recordYear.set($any($event.target).value)">
              <option value="">--</option>
              @for (y of academicYears; track y) { <option [value]="y.name">{{ y.name }}</option> }
            </select>
          </div> -->
          <div class="col-md-1"><label class="form-label small fw-semibold text-muted">From</label>
            <input type="date" class="form-control" [value]="recordDateFrom()" (input)="recordDateFrom.set($any($event.target).value)"></div>
          <div class="col-md-1"><label class="form-label small fw-semibold text-muted">To</label>
            <input type="date" class="form-control" [value]="recordDateTo()" (input)="recordDateTo.set($any($event.target).value)"></div>
          <div class="col-auto"><button class="btn btn-outline-secondary" (click)="resetRecordFilters()"><i class="bi bi-arrow-counterclockwise me-1"></i>Reset</button></div>
        </div>

        <!-- Summary Pills -->
        <div class="summary-pills mt-3">
          <div class="sp-item sp-total"><span class="sp-val">{{ collectionSummary().total }}</span><span class="sp-label">Total</span></div>
          <div class="sp-item sp-paid"><span class="sp-val">{{ collectionSummary().paid }}</span><span class="sp-label">Paid</span></div>
          <div class="sp-item sp-pending"><span class="sp-val">{{ collectionSummary().pending }}</span><span class="sp-label">Pending</span></div>
          <div class="sp-item sp-overdue"><span class="sp-val">{{ collectionSummary().overdue }}</span><span class="sp-label">Overdue</span></div>
          <div class="sp-item sp-refunded"><span class="sp-val">{{ collectionSummary().refunded }}</span><span class="sp-label">Refunded</span></div>
          <div class="sp-item sp-amount"><span class="sp-val">{{ formatCurrency(collectionSummary().collected) }}</span><span class="sp-label">Collected</span></div>
          <div class="sp-item sp-balance"><span class="sp-val">{{ formatCurrency(collectionSummary().outstanding) }}</span><span class="sp-label">Outstanding</span></div>
          <div class="sp-item sp-conc"><span class="sp-val">{{ formatCurrency(collectionSummary().concessions) }}</span><span class="sp-label">Concessions</span></div>
          <small class="ms-auto text-muted">Showing {{ filteredReceipts().length }} records</small>
        </div>
      </div>
    </div>

    <!-- Records Table -->
    <div class="fee-card">
      <div class="fc-body p-0">
        @if (filteredReceipts().length === 0) {
          <div class="empty-state"><i class="bi bi-table"></i><p>No records found</p><button class="btn btn-outline-secondary btn-sm" (click)="resetRecordFilters()">Clear filters</button></div>
        } @else {
          <div class="table-responsive">
            <table class="records-table">
              <thead><tr>
                <th>Receipt No.</th><th>Student</th><th>Class</th><th>Date</th>
                <th class="text-end">Total Due</th><th class="text-end">Concession</th><th class="text-end">Paid</th><th class="text-end">Refunded</th><th class="text-end">Net</th><th class="text-end">Balance</th>
                <th>Mode</th><th>Status</th><th>Payment</th><th class="text-center">Actions</th>
              </tr></thead>
              <tbody>
                @for (r of filteredReceipts(); track r.id) {
                  <tr>
                    <td class="rec-no font-monospace small">{{ r.receipt_no }}</td>
                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <div class="rec-avatar">{{ r.student_name.charAt(0) }}</div>
                        <div>
                          <div class="fw-semibold small">{{ r.student_name }}</div>
                          <div class="small text-muted">{{ r.student_id }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="small text-secondary">{{ r.class_name }}</td>
                    <td class="small text-muted">{{ formatDate(r.payment_date) }}</td>
                    <td class="text-end small">{{ formatCurrency(r.total_due) }}</td>
                    <td class="text-end small text-success">{{ r.total_concession > 0 ? formatCurrency(r.total_concession) : '—' }}</td>
                    <td class="text-end fw-semibold small">{{ formatCurrency(r.total_paid) }}</td>
                    <td class="text-end small" [class.text-purple]="r.refund_amount > 0">{{ r.refund_amount > 0 ? formatCurrency(r.refund_amount) : '—' }}</td>
                    <td class="text-end fw-semibold small">{{ formatCurrency(netPaid(r)) }}</td>
                    <td class="text-end small" [class.text-danger]="r.balance > 0">{{ r.balance > 0 ? formatCurrency(r.balance) : '—' }}</td>
                    <td>
                      <span class="mode-chip"><i class="bi {{ getModeIcon(r.payment_mode) }} me-1"></i>{{ r.payment_mode }}</span>
                    </td>
                    <td>
                      <span class="rec-status-badge {{ getStatusClass(r.status) }}">
                        <i class="bi {{ getStatusIcon(r.status) }} me-1"></i>{{ r.status }}
                      </span>
                    </td>
                    <td>
                      @if (r.payment_verified) {
                        <span class="verify-chip received"><i class="bi bi-shield-check me-1"></i>Received</span>
                      } @else {
                        <span class="verify-chip awaiting"><i class="bi bi-hourglass-split me-1"></i>Unverified</span>
                      }
                    </td>
                    <td class="text-center">
                      <div class="rec-actions">
                        <button class="ra" title="View Receipt" (click)="viewReceipt(r)"><i class="bi bi-eye-fill"></i></button>
                        <button class="ra" title="Fee ledger" (click)="openLedgerPerson(r.student_id)"><i class="bi bi-journal-text"></i></button>
                        @if (canCollectRemaining(r)) {
                          <button class="ra remain" title="Collect remaining" (click)="collectRemainingFrom(r)"><i class="bi bi-cash-stack"></i></button>
                        }
                        @if (canEditCollection(r)) {
                          <button class="ra" title="Edit collection" (click)="editCollection(r)"><i class="bi bi-pencil-fill"></i></button>
                        }
                        <button class="ra" title="Print" (click)="viewingReceipt.set(r); printReceipt()"><i class="bi bi-printer-fill"></i></button>
                        @if (!r.payment_verified && r.total_paid > 0 && r.status !== 'Refunded') {
                          <button class="ra verify" title="Mark payment received" (click)="verifyPayment(r.id)"><i class="bi bi-check2-circle"></i></button>
                        }
                        @if (canRefund(r)) {
                          <button class="ra refund" title="Refund fee" (click)="openRefund(r)"><i class="bi bi-arrow-counterclockwise"></i></button>
                        }
                        <button class="ra danger" title="Delete collection" (click)="deleteCollection(r)"><i class="bi bi-trash3-fill"></i></button>
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  }

  <!-- ===================================================
       TAB: FEE LEDGER
       =================================================== -->
  @if (activeTab() === 'ledger') {
    <!-- <div class="fee-card ledger-toolbar mb-3">
      <div class="fc-body">
        <div class="row g-3 align-items-start">
          <div class="col-md-5">
            <label class="form-label small fw-semibold text-muted">Ledger for</label>
            <div class="payment-mode-group">
              @for (opt of [{id:'person',l:'Person'},{id:'account',l:'Fee account'},{id:'institute',l:'Institute'}]; track opt.id) {
                <label class="pm-option" [class.selected]="ledgerScope() === opt.id" (click)="setLedgerScope($any(opt.id))">
                  <i class="bi d-block"
                    [class.bi-person-vcard]="opt.id === 'person'"
                    [class.bi-folder2-open]="opt.id === 'account'"
                    [class.bi-buildings]="opt.id === 'institute'"></i>
                  <span>{{ opt.l }}</span>
                </label>
              }
            </div>
          </div>
          <div class="col-md-2">
            <label class="form-label small fw-semibold text-muted">Academic Year</label>
            <select class="form-select" [value]="ledgerYear()" (change)="ledgerYear.set($any($event.target).value)">
              @for (y of academicYears; track y) { <option [value]="y.code" [selected]="y.code==ledgerYear()">{{ y.name }}</option> }
            </select>
          </div>
          @if (ledgerScope() === 'person') {
            <div class="col-md-4">
              <label class="form-label small fw-semibold text-muted">Student ID / Name</label>
              <div class="input-group">
                <input type="text" class="form-control" placeholder="e.g. S003, Rahul…"
                  [value]="ledgerQuery()" (input)="ledgerQuery.set($any($event.target).value)"
                  (keyup.enter)="loadLedgerPerson()">
                <button class="btn fee-btn-primary" (click)="loadLedgerPerson()"><i class="bi bi-search me-1"></i>Load</button>
              </div>
            </div>
          }
          @if (ledgerScope() === 'account') {
            <div class="col-md-4">
              <label class="form-label small fw-semibold text-muted">Fee account</label>
              <select class="form-select" [value]="ledgerAccount()" (change)="ledgerAccount.set($any($event.target).value)">
                <option value="">Select account</option>
                @for (a of ledgerAccountOptions(); track a.code) {
                  <option [value]="a.code">{{ a.code }} — {{ a.name }}</option>
                }
              </select>
            </div>
          }
          @if (ledgerScope() === 'institute') {
            <div class="col-md-4">
              <label class="form-label small fw-semibold text-muted">Institute</label>
              <select class="form-select" [value]="ledgerInstitute()" (change)="ledgerInstitute.set($any($event.target).value)">
                <option value="">Select institute</option>
                @for (n of ledgerInstituteOptions(); track n) { <option [value]="n">{{ n }}</option> }
              </select>
            </div>
          }
        </div>
      </div>
    </div> -->

    @if (ledgerStatement(); as st) {
      <div class="ledger-statement fee-card mb-3">
        <div class="fc-header d-flex justify-content-between">
          <span><i class="bi bi-journal-text me-2"></i>Fee Ledger Statement</span>
          <div class="d-flex gap-2">
            @if (st.scope === 'person' && st.remaining > 0) {
              <button type="button" class="btn btn-sm btn-light" (click)="collectFromLedger(st.parties[0].student_id)">
                <i class="bi bi-cash-stack me-1"></i>Collect remaining
              </button>
            }
            <button type="button" class="btn btn-sm btn-light" (click)="printLedger()"><i class="bi bi-printer-fill me-1"></i>Print</button>
            <button type="button" class="btn btn-sm btn-light" (click)="openLedgerPerson('')">Back</button>
          </div>
        </div>
        <div class="fc-body">
          <div class="ledger-head mb-3">
            <div>
              <div class="ledger-title">{{ st.title }}</div>
              <div class="ledger-sub">{{ st.subtitle }}</div>
            </div>
            <div class="text-end">
              <div class="small text-muted">Statement of account</div>
              <div class="fw-semibold">AY {{ st.academic_year }}</div>
            </div>
          </div>
          <div class="summary-pills mb-3">
            <div class="sp-item"><span class="sp-val">{{ formatCurrency(st.assessed) }}</span><span class="sp-label">Assessed</span></div>
            <div class="sp-item sp-amount"><span class="sp-val">{{ formatCurrency(st.payments) }}</span><span class="sp-label">Payments</span></div>
            <div class="sp-item sp-conc"><span class="sp-val">{{ formatCurrency(st.discounts) }}</span><span class="sp-label">Discounts</span></div>
            <div class="sp-item sp-waiver"><span class="sp-val">{{ formatCurrency(st.waivers) }}</span><span class="sp-label">Waivers</span></div>
            @if (st.late_fees > 0) {
              <div class="sp-item sp-overdue"><span class="sp-val">{{ formatCurrency(st.late_fees) }}</span><span class="sp-label">Late fees</span></div>
            }
            @if (st.refunds > 0) {
              <div class="sp-item sp-refunded"><span class="sp-val">{{ formatCurrency(st.refunds) }}</span><span class="sp-label">Refunds</span></div>
            }
            <div class="sp-item sp-balance"><span class="sp-val">{{ formatCurrency(st.remaining) }}</span><span class="sp-label">Remaining</span></div>
          </div>
          <div class="table-responsive">
            <table class="ledger-table">
              <thead>
                <tr>
                  <th width="100">Date</th>
                  <th>Particulars</th>
                  @if (st.scope !== 'person') { <th>Person</th> }
                  <th>Type</th>
                  <th>Ref</th>
                  <th class="text-end">Debit</th>
                  <th class="text-end">Credit</th>
                  <th class="text-end">Balance</th>
                </tr>
              </thead>
              <tbody>
                @for (e of st.entries; track e.id) {
                  <tr>
                    <td class="small text-muted">{{ formatDate(e.date) }}</td>
                    <td class="small">{{ getItShort(e.particulars) }}</td>
                    @if (st.scope !== 'person') {
                      <td class="small">{{ e.student_name }} <span class="text-muted">{{ e.student_id }}</span></td>
                    }
                    <td><span class="lk-chip {{ ledgerKindClass(e.kind) }}">{{ ledgerKindLabel(e.kind) }}</span></td>
                    <td class="small font-monospace">{{ e.ref_no || '—' }}</td>
                    <td class="text-end small">{{ e.debit ? formatCurrency(e.debit) : '—' }}</td>
                    <td class="text-end small text-success">{{ e.credit ? formatCurrency(e.credit) : '—' }}</td>
                    <td class="text-end small fw-semibold" [class.text-danger]="e.running_balance > 0">{{ formatCurrency(e.running_balance) }}</td>
                  </tr>
                }
              </tbody>
              <tfoot>
                <tr>
                  <td [attr.colspan]="st.scope === 'person' ? 5 : 6" class="fw-bold">Closing balance</td>
                  <td></td>
                  <td class="text-end fw-bold" [class.text-danger]="st.remaining > 0">{{ formatCurrency(st.remaining) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    } @else if (ledgerScope() === 'person') {
      <div class="fee-card">
        <!-- <div class="fc-header d-flex justify-content-between">
          <span><i class="bi bi-people me-2"></i>Accounts directory</span>
          <select class="form-select py-0" [value]="ledgerYear()" (change)="ledgerYear.set($any($event.target).value)"
            style="width: 150px;">
            @for (y of academicYears; track y) { <option [value]="y.code" [selected]="y.code==ledgerYear()">{{ y.name }}</option> }
          </select>
        </div> -->
        <div class="fc-body p-0">
          <div class="table-responsive">
            <table class="records-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Class</th>
                  <th>Fee account</th>
                  <!-- <th>Institute</th> -->
                  <th class="text-end">Assessed</th>
                  <th class="text-end">Paid</th>
                  <th class="text-end">Remaining</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                @for (p of ledgerDirectory(); track p.student_id) {
                  <tr>
                    <td>
                      <div class="fw-semibold small">{{ p.student_name }}</div>
                      <div class="small text-muted">Roll No.: {{ p.roll_no }}</div>
                    </td>
                    <td class="small">{{ dataService.getClassName(p.class_name) }} Class</td>
                    <td class="small font-monospace">{{ p.account_code }}</td>
                    <!-- <td class="small">{{ p.institute_name }}</td> -->
                    <td class="text-end small">{{ formatCurrency(p.assessed) }}</td>
                    <td class="text-end small">{{ formatCurrency(p.payments) }}</td>
                    <td class="text-end small" [class.text-danger]="p.remaining > 0">{{ formatCurrency(p.remaining) }}</td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-outline-primary" (click)="openLedgerPerson(p.student_id)">View ledger</button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    } @else {
      <div class="empty-state"><i class="bi bi-journal-text"></i><p>Select a {{ ledgerScope() === 'account' ? 'fee account' : 'institute' }} to open its ledger statement.</p></div>
    }
  }

  <!-- ===================================================
       TAB 4: CONCESSIONS
       =================================================== -->
  @if (activeTab() === 'concessions') {

    <!-- LIST -->
    @if (concessionFormMode === 'list') {
      @if (concessions().length === 0) {
        <div class="empty-state"><i class="bi bi-percent"></i><p>No concessions defined</p>
          <button class="btn fee-btn-primary btn-sm" (click)="openNewConcession()"><i class="bi bi-plus-circle me-1"></i>Add Concession</button></div>
      } @else {
        <div class="conc-grid">
          @for (c of concessions(); track c.id) {
            <div class="conc-card">
              <div class="cc-header">
                <div class="cc-icon"><i class="bi bi-percent"></i></div>
                <div>
                  <h6 class="cc-name">{{ c.concession_name }}</h6>
                  <span class="cc-type">{{ c.concession_type }}</span>
                </div>
                <div class="cc-value">
                  @if (c.concession_type === 'Percentage') { {{ c.c_value }}% }
                  @else { {{ formatCurrency(c.c_value) }} }
                </div>
              </div>
              <div class="cc-body">
                <div class="cc-row"><span class="cc-label">Applicable Heads</span><span class="cc-val">{{ c.applicable_heads.join(', ') || 'All heads' }}</span></div>
                <div class="cc-row"><span class="cc-label">Criteria</span><span class="cc-val text-muted">{{ c.criteria || '—' }}</span></div>
              </div>
              <div class="cc-footer">
                <button class="ccf-btn" [class.active]="c.is_active" (click)="toggleConcessionStatus(c)">
                  <i class="bi" [class.bi-toggle-on]="c.is_active" [class.bi-toggle-off]="!c.is_active"></i>
                  {{ c.is_active ? 'Active' : 'Inactive' }}
                </button>
                <button class="ccf-btn edit" (click)="editConcession(c)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
                <button class="ccf-btn delete" (click)="deleteConcession(c.id, c.concession_name)"><i class="bi bi-trash3-fill"></i></button>
              </div>
            </div>
          }
        </div>
      }
    }

    <!-- FORM -->
    @if (concessionFormMode === 'form') {
      <div class="fee-card" style="max-width:640px;">
        <div class="fc-header">
          <button class="btn btn-outline-secondary btn-sm me-3" (click)="concessionFormMode='list'"><i class="bi bi-arrow-left"></i></button>
          <i class="bi bi-percent me-2"></i>{{ editingConcessionId !== null ? 'Edit Concession' : 'New Concession' }}
        </div>
        <div class="fc-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label fw-semibold">Concession Name <span class="req">*</span></label>
              <input type="text" class="form-control" [(ngModel)]="concessionForm.concession_name" maxlength="80" placeholder="e.g. SC/ST Concession">
            </div>
            <div class="col-md-5">
              <label class="form-label fw-semibold">Type</label>
              <select class="form-select" [(ngModel)]="concessionForm.concession_type">
                @for (t of concessionTypes; track t) { <option [value]="t">{{ t }}</option> }
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold">Value</label>
              <div class="input-group">
                <input type="number" class="form-control" [(ngModel)]="concessionForm.c_value" min="0" placeholder="0">
                <span class="input-group-text bg-light">{{ concessionForm.concession_type === 'Percentage' ? '%' : '₹' }}</span>
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Status</label>
              <select class="form-select" [(ngModel)]="concessionForm.is_active">
                <option [value]="true">Active</option>
                <option [value]="false">Inactive</option>
              </select>
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold">Applicable Fee Heads <small class="text-muted fw-normal">(leave empty for all)</small></label>
              <div class="fh-checkgrid">
                @for (h of feeHeads().filter(h => h.is_active); track h.id) {
                  <label class="fhc-chip" [class.selected]="concessionForm.applicable_heads.includes(h.head_name)">
                    <input type="checkbox" class="d-none"
                      [checked]="concessionForm.applicable_heads.includes(h.head_name)"
                      (change)="toggleConcessionHead(h.head_name)">
                    {{ h.head_name }}
                  </label>
                }
              </div>
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold">Eligibility Criteria</label>
              <textarea class="form-control" [(ngModel)]="concessionForm.criteria" rows="2" maxlength="300" placeholder="Who qualifies for this concession?"></textarea>
            </div>
          </div>
          <div class="d-flex justify-content-end gap-3 mt-4">
            <button class="btn btn-outline-secondary px-4" (click)="concessionFormMode='list'">Cancel</button>
            <button class="btn fee-btn-primary px-5" (click)="saveConcession()">
              <i class="bi bi-check-circle-fill me-2"></i>{{ editingConcessionId !== null ? 'Update' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    }
  }

</div>
